/**
 * 상품-카테고리 링크 벌크 업로드 (비동기 잡) 타입
 *
 * 백엔드 스펙: amondz-backend-monorepo#4834
 * docs/design-docs/category-link-bulk-async-migration.html
 */

/**
 * 서버가 내려주는 잡 상태 (Bull JobState 미러 — 백엔드 job-status-response.type.ts 와 일치).
 * completed / failed 만 종결이고, 나머지 다섯은 전부 "아직 진행 중" 계열이다.
 */
export const BULK_UPLOAD_JOB_STATUS = {
  /** 대기 — 워커가 아직 집어가지 않음 */
  WAITING: 'waiting',
  /** 처리 중 — progress 0~99 갱신 */
  ACTIVE: 'active',
  /** DB 반영 완료 (ES 인덱스 동기화는 백그라운드로 계속 진행될 수 있음) */
  COMPLETED: 'completed',
  /** 잡 실행 실패 — failedReason 이 채워진다 */
  FAILED: 'failed',
  /** 재시도 대기 등으로 지연 — 곧 다시 active 가 된다 */
  DELAYED: 'delayed',
  /** 큐가 일시정지됨 — 운영 조치 중일 수 있으나 잡은 살아 있다 */
  PAUSED: 'paused',
  /** 워커가 응답하지 않아 Bull 이 stalled 로 판정 — 재개되거나 실패로 넘어간다 */
  STUCK: 'stuck',
} as const;

export type BulkUploadJobStatus = (typeof BULK_UPLOAD_JOB_STATUS)[keyof typeof BULK_UPLOAD_JOB_STATUS];

/**
 * 잡 조회가 404 를 받았을 때 프론트가 붙이는 상태.
 * 서버가 내려주는 값이 아니다 — 완료 잡은 24시간 후 Bull 에서 삭제되고,
 * 다른 어드민의 잡도 (존재 여부를 숨기기 위해) 404 로 온다.
 */
export const BULK_UPLOAD_JOB_EXPIRED = 'expired' as const;

export type BulkUploadJobResult = {
  totalRows: number;
  /** 신규 INSERT 성공 */
  successRows: number;
  /** 이미 존재해서 INSERT 하지 않은 행 — 실패가 아니라 성공으로 카운트한다 */
  alreadyExistsRows: number;
  /** 검증 실패 + 트랜잭션 실패 */
  failRows: number;
  failedRowsAvailable: boolean;
};

/** ES 검색 인덱스 동기화 집계 상태 (EsSyncOutbox 를 correlationId 로 집계) */
export const BULK_UPLOAD_ES_SYNC_STATUS = {
  /** 동기화할 상품이 없음 (성공 0건) */
  NOT_REQUESTED: 'not_requested',
  PENDING: 'pending',
  ENQUEUED: 'enqueued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type BulkUploadEsSyncStatus = (typeof BULK_UPLOAD_ES_SYNC_STATUS)[keyof typeof BULK_UPLOAD_ES_SYNC_STATUS];

export type BulkUploadEsSyncSummary = {
  status: BulkUploadEsSyncStatus;
  outboxRows: number;
  totalProducts: number;
  pendingProducts: number;
  enqueuedProducts: number;
  processingProducts: number;
  completedProducts: number;
  failedProducts: number;
};

export type BulkUploadJobStatusResponse = {
  jobId: string;
  correlationId: string;
  status: BulkUploadJobStatus;
  /** 0~100 */
  progress: number;
  /** completed 일 때만 채워진다 */
  result: BulkUploadJobResult | null;
  /** failed 일 때 채워진다. 원시 에러 코드('EXCELUPLOAD008')이거나 자유 문장 */
  failedReason: string | null;
  /**
   * ES 검색 인덱스 동기화 집계. 백엔드는 항상 실어 주지만(#4914 컨트롤러 확인)
   * Swagger 상 optional 이라 방어적으로 optional 로 둔다.
   */
  esSync?: BulkUploadEsSyncSummary;
};

export type BulkUploadExpiredJob = {
  status: typeof BULK_UPLOAD_JOB_EXPIRED;
};

/** 폴링 쿼리가 캐시에 담는 값 */
export type BulkUploadJobQueryData = BulkUploadJobStatusResponse | BulkUploadExpiredJob;

/** POST 202 Accepted */
export type BulkUploadAcceptedResponse = {
  jobId: string;
  correlationId: string;
  message: string;
};

/** localStorage 에 보관하는 진행 중 잡 정보 — 페이지 재진입 시 복원용 */
export type BulkUploadJobRecord = {
  jobId: string;
  fileName: string;
  /** 폴링 간격 계산 기준 시각 (epoch ms) */
  startedAt: number;
};
